import { useContext } from 'react';
import { TenantContext } from '../../../context/tenant-context';
import {
  CalculateOverageChargeRequest,
  CalculateOverageChargeResult,
  CreatePartRequest,
  FuelChargingConfig,
  HourTrackingConfig,
  MinimumDurationPrice,
  Product,
  ProductDepreciationPercentage,
  ProductImage,
  ProductInventoryUnit,
  ProductLocationStock,
  SearchProductsRequest,
  UpdateProductLocationStockRequest,
  UpdateProductUnitRequest,
  UpsertMinimumDurationPriceRequest,
} from './product';
import { ProductParsers } from './product-parsers';
import { handleRequest } from '../quipli-service-client';
import CalculateOverageChargeResultJson = ProductParsers.CalculateOverageChargeResultJson;
import { PaginationParsers } from '../pagination/pagination-parsers';
import { Pagination } from '../pagination/pagination';

export const useProductApiV1 = () => {
  const { tenant, contextPath } = useContext(TenantContext);

  async function getProductUnit(productId: number, unitId: number): Promise<ProductInventoryUnit> {
    const config = {
      method: 'get',
      url: `${contextPath}quipli-service/yorent/v1/products/${productId}/units/${unitId}`,
      headers: {
        'X-Tenant': tenant,
      },
    };
    const result = await handleRequest<ProductParsers.ProductInventoryUnitJson>(config);
    return ProductParsers.toProductInventoryUnit(result);
  }

  async function getProduct(productId: number): Promise<Product> {
    const config = {
      method: 'get',
      url: `${contextPath}quipli-service/yorent/v1/products/${productId}`,
      headers: {
        'X-Tenant': tenant,
      },
    };
    const result = await handleRequest<ProductParsers.ProductJson>(config);
    return ProductParsers.toProduct(result);
  }

  async function createPart(body: CreatePartRequest): Promise<Product> {
    const config = {
      method: 'post',
      url: `${contextPath}quipli-service/yorent/v1/products/part`,
      headers: {
        'X-Tenant': tenant,
      },
      data: body,
    };
    const result = await handleRequest<ProductParsers.ProductJson>(config);

    return ProductParsers.toProduct(result);
  }

  async function searchParts(data: SearchProductsRequest): Promise<Pagination<Product>> {
    const config = {
      method: 'post',
      url: `${contextPath}quipli-service/yorent/v1/products/searchParts`,
      headers: {
        'X-Tenant': tenant,
      },
      data,
    };
    const { items, pagination } = await handleRequest<PaginationParsers.PaginationJson<ProductParsers.ProductJson>>(
      config,
    );
    return {
      pagination,
      items: items.map(ProductParsers.toProduct),
    };
  }

  async function searchServices(data: SearchProductsRequest): Promise<Pagination<Product>> {
    const config = {
      method: 'post',
      url: `${contextPath}quipli-service/yorent/v1/products/searchServices`,
      headers: {
        'X-Tenant': tenant,
      },
      data,
    };
    const { items, pagination } = await handleRequest<PaginationParsers.PaginationJson<ProductParsers.ProductJson>>(
      config,
    );
    return {
      pagination,
      items: items.map(ProductParsers.toProduct),
    };
  }

  async function listProductLocationStocks(productIds: number[], locationId?: number): Promise<ProductLocationStock[]> {
    const config = {
      method: 'post',
      url: `${contextPath}quipli-service/yorent/v1/products/stocks`,
      headers: {
        'X-Tenant': tenant,
      },
      data: {
        productIds,
        locationId,
      },
    };
    const result = await handleRequest<ProductParsers.ProductLocationStockJson[]>(config);
    return result.map((r) => ProductParsers.toProductLocationStock(r));
  }

  async function updateProductUnit(
    productId: number,
    unitId: number,
    update: UpdateProductUnitRequest,
  ): Promise<ProductInventoryUnit> {
    const config = {
      method: 'put',
      url: `${contextPath}quipli-service/yorent/v1/products/${productId}/units/${unitId}`,
      headers: {
        'X-Tenant': tenant,
      },
      data: update,
    };
    const result = await handleRequest<ProductParsers.ProductInventoryUnitJson>(config);
    return ProductParsers.toProductInventoryUnit(result);
  }

  async function createProductUnit(productId: number, update: UpdateProductUnitRequest): Promise<ProductInventoryUnit> {
    const config = {
      method: 'post',
      url: `${contextPath}quipli-service/yorent/v1/products/${productId}/units`,
      headers: {
        'X-Tenant': tenant,
      },
      data: update,
    };
    const result = await handleRequest<ProductParsers.ProductInventoryUnitJson>(config);
    return ProductParsers.toProductInventoryUnit(result);
  }

  async function deleteProductUnit(productId: number, unitId: number): Promise<void> {
    const config = {
      method: 'delete',
      url: `${contextPath}quipli-service/yorent/v1/products/${productId}/units/${unitId}`,
      headers: {
        'X-Tenant': tenant,
      },
    };
    return await handleRequest<void>(config);
  }

  async function updateProductLocationStocks(
    productId: number,
    update: UpdateProductLocationStockRequest,
  ): Promise<Product> {
    const config = {
      method: 'patch',
      url: `${contextPath}quipli-service/yorent/v1/products/${productId}/stock`,
      headers: {
        'X-Tenant': tenant,
      },
      data: update,
    };
    const result = await handleRequest<ProductParsers.ProductJson>(config);
    return ProductParsers.toProduct(result);
  }

  async function getUploadProductImageUrl(
    productId: number,
    filename: string,
  ): Promise<{ url: string; imageId: string }> {
    const config = {
      method: 'post',
      url: `${contextPath}quipli-service/yorent/v1/products/${productId}/images/${filename}`,
      headers: {
        'X-Tenant': tenant,
      },
    };
    return await handleRequest<{ url: string; imageId: string }>(config);
  }

  async function addProductImage(
    productId: number,
    body: { imageId: string; filename: string; rank: number; altTitle?: string },
  ): Promise<ProductImage> {
    const config = {
      method: 'post',
      url: `${contextPath}quipli-service/yorent/v1/products/${productId}/images`,
      headers: {
        'X-Tenant': tenant,
      },
      data: body,
    };
    return await handleRequest<ProductImage>(config);
  }

  async function updateProductImages(
    productId: number,
    images: { id: string; rank: number; altTitle?: string }[],
  ): Promise<void> {
    const config = {
      method: 'put',
      url: `${contextPath}quipli-service/yorent/v1/products/${productId}/images`,
      headers: {
        'X-Tenant': tenant,
      },
      data: images,
    };
    return await handleRequest<void>(config);
  }

  async function getUploadProductPlaceholderImageUrl(filename: string): Promise<string> {
    const config = {
      method: 'post',
      url: `${contextPath}quipli-service/yorent/v1/products/images/placeholder/${filename}`,
      headers: {
        'X-Tenant': tenant,
      },
    };
    return await handleRequest<string>(config);
  }

  async function uploadResizedPlaceholderImages(): Promise<void> {
    const config = {
      method: 'post',
      url: `${contextPath}quipli-service/yorent/v1/products/images/placeholder`,
      headers: {
        'X-Tenant': tenant,
      },
    };
    return await handleRequest<void>(config);
  }

  async function getFuelChargingConfig(productId: number): Promise<FuelChargingConfig> {
    const config = {
      method: 'get',
      url: `${contextPath}quipli-service/yorent/v1/products/${productId}/fuelChargingConfig`,
      headers: {
        'X-Tenant': tenant,
      },
    };
    return await handleRequest<FuelChargingConfig>(config);
  }

  async function updateFuelChargingConfig(fcc: FuelChargingConfig): Promise<FuelChargingConfig> {
    const config = {
      method: 'post',
      url: `${contextPath}quipli-service/yorent/v1/products/fuelChargingConfig`,
      headers: {
        'X-Tenant': tenant,
      },
      data: fcc,
    };
    return await handleRequest<FuelChargingConfig>(config);
  }

  async function deleteFuelChargingConfig(productId: number): Promise<void> {
    const config = {
      method: 'delete',
      url: `${contextPath}quipli-service/yorent/v1/products/${productId}/fuelChargingConfig`,
      headers: {
        'X-Tenant': tenant,
      },
    };
    return await handleRequest<void>(config);
  }

  async function getHourTrackingConfigs(productIds: number[]): Promise<HourTrackingConfig[]> {
    const config = {
      method: 'post',
      url: `${contextPath}quipli-service/yorent/v1/products/hourTrackingConfig/multiple`,
      headers: {
        'X-Tenant': tenant,
      },
      data: { productIds },
    };
    return await handleRequest<HourTrackingConfig[]>(config);
  }

  async function upsertHourTrackingConfig(htc: HourTrackingConfig): Promise<HourTrackingConfig> {
    const config = {
      method: 'post',
      url: `${contextPath}quipli-service/yorent/v1/products/${htc.productId}/hourTrackingConfig`,
      headers: {
        'X-Tenant': tenant,
      },
      data: htc,
    };
    return await handleRequest<HourTrackingConfig>(config);
  }

  async function deleteHourTrackingConfig(productId: number): Promise<void> {
    const config = {
      method: 'delete',
      url: `${contextPath}quipli-service/yorent/v1/products/${productId}/hourTrackingConfig`,
      headers: {
        'X-Tenant': tenant,
      },
    };
    return await handleRequest<void>(config);
  }

  async function generateAiDescription(input: string): Promise<string> {
    const config = {
      method: 'get',
      url: `${contextPath}quipli-service/yorent/v1/products/generateProductDescription?input=${input}`,
    };

    return await handleRequest<string>(config);
  }

  async function getProductMinimumDurationPrice(productId: number): Promise<MinimumDurationPrice | undefined> {
    const config = {
      method: 'get',
      url: `${contextPath}quipli-service/yorent/v1/products/${productId}/minimumDurationPrice`,
      headers: {
        'X-Tenant': tenant,
      },
    };
    return handleRequest<MinimumDurationPrice>(config);
  }

  async function upsertProductMinimumDurationPrice(
    req: UpsertMinimumDurationPriceRequest,
  ): Promise<MinimumDurationPrice> {
    const config = {
      method: 'post',
      url: `${contextPath}quipli-service/yorent/v1/products/${req.productId}/minimumDurationPrice`,
      headers: {
        'X-Tenant': tenant,
      },
      data: req,
    };

    return handleRequest<MinimumDurationPrice>(config);
  }

  async function deleteProductMinimumDurationPrice(productId: number): Promise<void> {
    const config = {
      method: 'delete',
      url: `${contextPath}quipli-service/yorent/v1/products/${productId}/minimumDurationPrice`,
      headers: {
        'X-Tenant': tenant,
      },
    };

    return handleRequest<void>(config);
  }

  async function calculateHourOverageCharge(
    requests: CalculateOverageChargeRequest[],
  ): Promise<CalculateOverageChargeResult[]> {
    const config = {
      method: 'post',
      url: `${contextPath}quipli-service/yorent/v1/products/calculateHourOverageCharge`,
      headers: {
        'X-Tenant': tenant,
      },
      data: requests,
    };

    const json = await handleRequest<CalculateOverageChargeResultJson[]>(config);
    return json.map(ProductParsers.toCalculateOverageChargeResult);
  }

  async function upsertProductDepreciationPercentage(
    productId: number,
    percentage: number,
  ): Promise<ProductDepreciationPercentage> {
    const config = {
      method: 'put',
      url: `${contextPath}quipli-service/yorent/v1/products/${productId}/depreciationPercentage`,
      headers: {
        'X-Tenant': tenant,
      },
      data: { percentage },
    };

    const json = await handleRequest<ProductParsers.ProductDepreciationPercentageJson>(config);
    return ProductParsers.toProductDepreciationPercentage(json);
  }

  async function getProductDepreciationPercentage(
    productId: number,
  ): Promise<ProductDepreciationPercentage | undefined> {
    const config = {
      method: 'get',
      url: `${contextPath}quipli-service/yorent/v1/products/${productId}/depreciationPercentage`,
      headers: {
        'X-Tenant': tenant,
      },
    };

    const json = await handleRequest<ProductParsers.ProductDepreciationPercentageJson | undefined>(config);
    if (!json) {
      return undefined;
    }
    return ProductParsers.toProductDepreciationPercentage(json);
  }

  async function deleteProductDepreciationPercentage(productId: number): Promise<void> {
    const config = {
      method: 'delete',
      url: `${contextPath}quipli-service/yorent/v1/products/${productId}/depreciationPercentage`,
      headers: {
        'X-Tenant': tenant,
      },
    };

    return handleRequest<void>(config);
  }

  async function getCustomProduct() {
    const config = {
      method: 'post',
      url: `${contextPath}quipli-service/yorent/v1/products/custom`,
      headers: {
        'X-Tenant': tenant,
      },
    };

    const result = await handleRequest<ProductParsers.ProductJson>(config);
    return ProductParsers.toProduct(result);
  }

  return {
    getProduct,
    searchParts,
    createPart,
    searchServices,
    listProductLocationStocks,
    getProductUnit,
    updateProductUnit,
    deleteProductUnit,
    createProductUnit,
    updateProductLocationStocks,
    getUploadProductImageUrl,
    addProductImage,
    updateProductImages,
    getUploadProductPlaceholderImageUrl,
    uploadResizedPlaceholderImages,
    getFuelChargingConfig,
    updateFuelChargingConfig,
    deleteFuelChargingConfig,
    getHourTrackingConfigs,
    upsertHourTrackingConfig,
    deleteHourTrackingConfig,
    generateAiDescription,
    getProductMinimumDurationPrice,
    upsertProductMinimumDurationPrice,
    deleteProductMinimumDurationPrice,
    calculateHourOverageCharge,
    upsertProductDepreciationPercentage,
    getProductDepreciationPercentage,
    deleteProductDepreciationPercentage,
    getCustomProduct,
  };
};
